// src/components/admin/OrganizationManager.jsx

import React, { useState, useEffect } from 'react';
import { copyFor, isCorporateOrg } from '../../data/copyDictionary';
import {
    Building2, Plus, Pencil, Share2, Trash2, Users, Loader2, X, Upload, Palette, Eye,
    ClipboardList, Clock, AlertTriangle, CheckCircle2, Briefcase, HeartHandshake, LifeBuoy, Wallet, Footprints, Landmark
} from 'lucide-react';
import { db, storage, functions } from '../../config/firebase-config';
import { httpsCallable } from 'firebase/functions';
import { 
    collection, deleteDoc, doc, onSnapshot, updateDoc, setDoc,
    arrayUnion, arrayRemove, query, where, getDocs 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; 
import { ASSESSMENT_LIBRARY } from '../../data/assessment-config';
import { DEFAULT_MEMBER_TYPES, resolveMemberTypes } from '../../data/memberTypes';
import { SPORT_OPTIONS, inferSportFromName, DEFAULT_SPORT } from '../../data/sports';
import { parseServicesInput, serializeServicesInput } from '../../utils/eapProgram';
import { ORG_THEMES } from '../../utils/orgTheme';
import { PATH_CHARACTER_OPTIONS, DEFAULT_PATH_CHARACTER, resolvePathCharacter } from '../../data/pathCharacters';
import { SUPPORT_PATHWAY_OPTIONS } from '../../data/supportPathways';
import { YOUTH_SURFACE_OPTIONS } from '../../utils/resolveSurface';
import { EDITIONS, DEFAULT_EDITION, resolveEditionId } from '../../data/editions';
import { sortOrgs } from '../../utils/orgOrder';
import { newStaffCode } from '../../utils/teams';

// --- MEMBER TYPES (JOIN FLOW PERSONAS) ---
// The three ids are FIXED SLOTS and purely TAGS (segmentation/reporting) —
// admins edit label/expects/surface, never the id. What ROUTES is the
// `surface` field: the adult slots pick between the adult member surface and
// the family/caregiver hub (e.g. NHL Alumni spouse -> family, Blue Cross
// spouse -> athlete). The child slot is always age-gated — age decides
// kidz/teen, so its surface is pinned to 'child' and isn't a choice.
const ADULT_SURFACE_OPTIONS = [
    { value: 'athlete', label: 'Adult member surface' },
    { value: 'family',  label: 'Family / caregiver hub' },
    { value: 'parent',  label: 'Parent hub (youth sport)' }
];
const defaultMemberTypeSlots = () => DEFAULT_MEMBER_TYPES.map(t => ({ ...t, enabled: true }));

// --- SUB-COMPONENT: ROSTER AUDIT (ANONYMOUS & SQUAD INSIGHTS) ---
const RosterAudit = ({ org, onClose }) => {
    const [users, setUsers] = useState([]);
    const [groupInsights, setGroupInsights] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    // Move a member: team or member type. The backstop for a wrong link or a
    // wrong tap at join. Allowed by rules for the org's admins and the super admin.
    const moveMember = async (uid, changes) => {
        try {
            await updateDoc(doc(db, 'users', uid), changes);
            setUsers(list => list.map(u => (u.id === uid ? { ...u, ...changes } : u)));
        } catch (e) { alert('Could not update this member: ' + e.message); }
    };
    const memberTypeOptions = resolveMemberTypes(org).filter(t => t.id !== 'child' || true);

    const getFriendlyLabel = (key) => {
        const labels = {
            'focus': 'Focus', 'resilience': 'Resilience', 'motivation': 'Motivation',
            'confidence': 'Confidence', 'discipline': 'Discipline', 'awareness': 'Self-Awareness',
            'adaptability': 'Adaptability', 'sleep_perf': 'Sleep Quality',
            'nutrition': 'Nutrition', 'injury_mgmt': 'Pain Management'
        };
        return labels[key] || key;
    };

    useEffect(() => {
        const fetchUsersAndInsights = async () => {
            try {
                const q = query(collection(db, "users"), where("organizationId", "==", org.id));
                const snap = await getDocs(q);
                
                const fetchedUsers = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                
                // 1. Sort users by newest assessment first for the table
                fetchedUsers.sort((a, b) => {
                    const dateA = a.lastAssessmentDate ? new Date(a.lastAssessmentDate).getTime() : 0;
                    const dateB = b.lastAssessmentDate ? new Date(b.lastAssessmentDate).getTime() : 0;
                    return dateB - dateA;
                });
                
                setUsers(fetchedUsers);

                // 2. Squad Insights (Bottom 3 Metrics) — individual scores are
                // locked to each user, so averages come from the server-side
                // aggregation endpoint (cohort averages only, small groups
                // suppressed).
                const aggFn = httpsCallable(functions, 'getGroupAggregates');
                const res = await aggFn({ orgId: org.id });

                const groupBottom3 = {};
                Object.entries(res.data?.groups || {}).forEach(([gId, g]) => {
                    const averages = [...(g.metrics || [])].sort((a, b) => b.score - a.score);
                    groupBottom3[gId] = [...averages].reverse().slice(0, 3);
                });

                setGroupInsights(groupBottom3);

            } catch (error) {
                console.error("Audit Fetch Error:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchUsersAndInsights();
    }, [org.id]);

    return (
        <div className="fixed inset-0 z-[400] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                    <div>
                        <h3 className="font-black text-xl text-slate-800 flex items-center gap-2">
                            <ClipboardList className="text-brand-primary"/> 
                            Diagnostic Audit: {org.name}
                        </h3>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">
                            Anonymized Data View • {users.length} Total Users
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors shadow-sm border border-slate-200"><X size={20} /></button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 custom-scrollbar">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12"><Loader2 className="animate-spin text-brand-primary" size={32}/></div>
                    ) : (
                        <div className="space-y-6">
                            
                            {/* --- SQUAD INSIGHTS (BOTTOM 3) --- */}
                            {Object.keys(groupInsights).length > 0 && (
                                <div>
                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <AlertTriangle size={14} className="text-orange-500" /> Squad Focus Areas (Lowest Averages)
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {Object.entries(groupInsights).map(([gId, bottom3]) => (
                                            <div key={gId} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-orange-200 transition-colors">
                                                <h4 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2 pb-2 border-b border-slate-100">
                                                    <Users size={16} className="text-brand-primary"/> {gId}
                                                </h4>
                                                {bottom3.length > 0 ? (
                                                    <div className="space-y-2">
                                                        {bottom3.map(m => (
                                                            <div key={m.id} className="flex justify-between items-center bg-orange-50 px-3 py-1.5 rounded-lg text-xs border border-orange-100">
                                                                <span className="font-bold text-orange-900">{getFriendlyLabel(m.id)}</span>
                                                                <span className="font-black text-orange-600">{m.score}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="p-4 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Awaiting Data</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* --- ANONYMOUS ROSTER TABLE --- */}
                            <div>
                                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <ClipboardList size={14} /> {copyFor('auditLog', isCorporateOrg(org))}
                                </h4>
                                <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-slate-50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
                                            <tr>
                                                <th className="p-4 font-bold">Anonymous ID</th>
                                                <th className="p-4 font-bold">Role</th>
                                                <th className="p-4 font-bold">{copyFor('groupUnit', isCorporateOrg(org))}</th>
                                                <th className="p-4 font-bold">Type</th>
                                                <th className="p-4 font-bold">Age</th>
                                                <th className="p-4 font-bold">Consent</th>
                                                <th className="p-4 font-bold">Last Assessment</th>
                                                <th className="p-4 font-bold">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {users.map((u) => {
                                                const isOverdue = u.lastAssessmentDate 
                                                    ? (new Date() - new Date(u.lastAssessmentDate)) / (1000*60*60*24) >= (org.cadenceDays || 30)
                                                    : true;
                                                
                                                return (
                                                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                                                        <td className="p-4 font-mono text-slate-600 font-bold">User-{u.id.slice(-5).toUpperCase()}</td>
                                                        <td className="p-4">
                                                            <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-[10px] font-bold uppercase">{u.role || 'User'}</span>
                                                        </td>
                                                        <td className="p-4 font-medium text-slate-700">
                                                            {u.role === 'user' || !u.role ? (
                                                                <select className="p-1.5 border border-slate-200 rounded-lg text-xs bg-white max-w-[220px]" value={u.groupId || ''} onChange={e => moveMember(u.id, { groupId: e.target.value || null })} title="Move to another team">
                                                                    <option value="">-- none --</option>
                                                                    {(org.groups || []).map(g => <option key={g} value={g}>{g}</option>)}
                                                                </select>
                                                            ) : (u.groupId || '--')}
                                                        </td>
                                                        <td className="p-4">
                                                            {u.role === 'user' || !u.role ? (
                                                                <select className="p-1.5 border border-slate-200 rounded-lg text-xs bg-white" value={u.memberType || ''} onChange={e => moveMember(u.id, { memberType: e.target.value || null })} title="Change member type">
                                                                    <option value="">--</option>
                                                                    {memberTypeOptions.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                                                </select>
                                                            ) : <span className="text-slate-400 text-xs">staff</span>}
                                                        </td>
                                                        <td className="p-4 text-xs font-bold text-slate-600">{u.ageTier ? (u.ageTier === 'kid' ? 'Under 13' : u.ageTier === 'teen' ? '13–17' : 'Adult') : '--'}</td>
                                                        <td className="p-4 text-xs font-bold">
                                                            {(u.ageTier === 'kid' || u.ageTier === 'teen') ? (
                                                                u.guardianConsent?.at
                                                                    ? <span className="text-green-600">{new Date(u.guardianConsent.at).toLocaleDateString()} · {u.guardianConsent.relationship || 'guardian'}</span>
                                                                    : <span className="text-orange-500">Missing</span>
                                                            ) : <span className="text-slate-300">n/a</span>}
                                                        </td>
                                                        <td className="p-4 text-slate-600 font-medium">
                                                            {u.lastAssessmentDate ? new Date(u.lastAssessmentDate).toLocaleDateString() : 'Never'}
                                                        </td>
                                                        <td className="p-4">
                                                            {!u.lastAssessmentDate ? (
                                                                <span className="flex items-center gap-1.5 text-slate-500 text-xs font-bold"><Clock size={14}/> Pending</span>
                                                            ) : isOverdue ? (
                                                                <span className="flex items-center gap-1.5 text-orange-500 text-xs font-bold"><AlertTriangle size={14}/> Overdue</span>
                                                            ) : (
                                                                <span className="flex items-center gap-1.5 text-green-500 text-xs font-bold"><CheckCircle2 size={14}/> Active</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                            {users.length === 0 && (
                                                <tr><td colSpan="8" className="p-8 text-center text-slate-400">No users found in this organization.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: GROUP MANAGER ---
const GroupManager = ({ org, onClose }) => {
    const [newGroup, setNewGroup] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);

    const handleAddGroup = async (e) => {
        e.preventDefault();
        if (!newGroup.trim()) return;
        setIsUpdating(true);
        try {
            const orgRef = doc(db, "organizations", org.id);
            await updateDoc(orgRef, { groups: arrayUnion(newGroup.trim()) });
            setNewGroup("");
        } catch (error) { alert("Error: " + error.message);
        }
        setIsUpdating(false);
    };
    const handleRemoveGroup = async (groupName) => {
        if (!window.confirm(`Remove subgroup "${groupName}"?`)) return;
        try {
            const orgRef = doc(db, "organizations", org.id);
            await updateDoc(orgRef, { groups: arrayRemove(groupName) });
        } catch (error) { alert("Error: " + error.message); }
    };
    return (
        <div className="fixed inset-0 z-[400] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div><h3 className="font-black text-xl text-slate-800">{org.name}</h3><p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Manage Sub-Groups</p></div>
                    <button onClick={onClose} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><X size={20} /></button>
                </div>
                <div className="p-6">
                    <form onSubmit={handleAddGroup} className="flex gap-2 mb-6">
                        <input className="flex-1 p-3 bg-slate-50 border-2 border-slate-100 rounded-xl font-medium outline-none focus:border-brand-primary focus:bg-white transition-all" placeholder={copyFor('groupPlaceholder', isCorporateOrg(org))} value={newGroup} onChange={(e) => setNewGroup(e.target.value)} autoFocus />
                        <button disabled={isUpdating} type="submit" className="px-4 bg-slate-900 text-white rounded-xl hover:bg-black transition-colors flex items-center justify-center">{isUpdating ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}</button>
                    </form>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                        {org.groups && org.groups.length > 0 ? (
                            org.groups.map((g, idx) => (
                                <div key={idx} className="flex justify-between items-center p-3 bg-white border border-slate-100 rounded-xl shadow-sm group hover:border-brand-primary/30 transition-all">
                                    <span className="font-bold text-slate-700 flex items-center gap-2"><Users size={16} className="text-slate-300 group-hover:text-brand-primary"/> {g}</span>
                                    <button onClick={() => handleRemoveGroup(g)} className="text-slate-300 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                                </div>
                            ))
                        ) : (<div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-100 rounded-xl">No sub-groups defined yet.</div>)}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const OrganizationManager = ({ onPreview }) => { 
    const [orgs, setOrgs] = useState([]);
    const [editingOrgId, setEditingOrgId] = useState(null);

    // The STAFF SIGN-UP CODE: a team's join link plus /staff/{code} lets bench staff
    // join onto that team's coaches roster (functions/modules/staffRules.js). It
    // lives in organizations/{id}/secrets/staff_join, a subcollection closed to
    // members, because the org doc itself is world-readable by id.
    const [staffCode, setStaffCode] = useState(null);
    useEffect(() => {
        if (!editingOrgId) { setStaffCode(null); return undefined; }
        return onSnapshot(doc(db, 'organizations', editingOrgId, 'secrets', 'staff_join'),
            snap => setStaffCode(snap.exists() ? (snap.data().code || null) : null),
            () => setStaffCode(null));
    }, [editingOrgId]);
    const rotateStaffCode = async () => {
        if (!editingOrgId) return;
        if (staffCode && !window.confirm('Rotate the staff code? Staff links already sent out stop working. Staff who have joined keep their access.')) return;
        try {
            await setDoc(doc(db, 'organizations', editingOrgId, 'secrets', 'staff_join'), { code: newStaffCode(), rotatedAt: new Date().toISOString() });
        } catch (e) { alert('Could not save the staff code: ' + e.message); }
    };
    const [showOrgModal, setShowOrgModal] = useState(false);
    
    // FORM STATE (Added industry)
    const [orgFormData, setOrgFormData] = useState({
        name: '',
        industry: 'sport', // DEFAULT TO SPORT
        sport: '', // sport vocabulary (data/sports.js); '' = infer from the name
        edition: 'sport', // enterprise-console vertical skin (generic per industry)
        pathCharacter: DEFAULT_PATH_CHARACTER, // Guided Path traveller (data/pathCharacters.js)
        supportPathwaysId: '', // Help & Support Pathways set (data/supportPathways.js); '' = none
        youthSurface: 'age', // where under-18s land (utils/resolveSurface.js)
        assessmentByMemberType: { spouse: '' }, // '' = same as adults
        aiCoachForMinors: true,
        youthContent: { hideMentalHealthUnder13: false },
        activeAssessmentId: 'core_v1',
        // Per-age-tier assessment overrides. Empty string = use the safe age
        // default (youth_v1) resolved in resolveAssessmentId. Adult tier always
        // uses activeAssessmentId above.
        assessmentByTier: { kid: '', teen: '' },
        cadenceDays: 30,
        adminEmails: '',
        coaches: [],
        // Fixed member-type slots (id never changes — only label/expects/enabled).
        memberTypes: defaultMemberTypeSlots()
    });

    // FEATURES STATE
    const [featuresData, setFeaturesData] = useState({
        enableSquadHub: false,
        enableFinanceHub: false,
        enableRecoveryModel: false
    });

    // BRANDING STATE
    const [brandingData, setBrandingData] = useState({
        enabled: false,
        primaryColor: '#0679af', 
        accentColor: '#0ea5e9',  
        darkColor: '#0f172a',    
        sidebarTextColor: '#ffffff', 
        lightColor: '#f0f9ff',   
        logoUrl: '',
        theme: 'classic', hubName: '', highlightColor: ''
    });
    const [logoFile, setLogoFile] = useState(null);
    const [eapLogoFile, setEapLogoFile] = useState(null);

    // SUPPORT CONTACTS STATE — the per-org "personal connection outlet(s)" surfaced
    // to members via the dashboard "Talk to Someone" modal. Each contact is tagged
    // with the member types it serves (empty = everyone / default), so e.g. former
    // players reach one person while spouses and kids reach another.
    const [supportContacts, setSupportContacts] = useState([]);
    const [contactPhotoFiles, setContactPhotoFiles] = useState({}); // { [idx]: File }

    const addSupportContact = () => setSupportContacts(prev => [...prev,
        { name: '', role: '', email: '', phone: '', bookingUrl: '', message: '', photoUrl: '', memberTypes: [] }]);
    const updateSupportContact = (idx, patch) =>
        setSupportContacts(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
    const removeSupportContact = (idx) => {
        setSupportContacts(prev => prev.filter((_, i) => i !== idx));
        // Re-index the pending photo files so they still line up with their rows.
        setContactPhotoFiles(prev => {
            const next = {};
            Object.entries(prev).forEach(([k, v]) => {
                const ki = Number(k);
                if (ki < idx) next[ki] = v; else if (ki > idx) next[ki - 1] = v;
            });
            return next;
        });
    };
    const toggleContactMemberType = (idx, typeId) => setSupportContacts(prev => prev.map((c, i) => {
        if (i !== idx) return c;
        const has = (c.memberTypes || []).includes(typeId);
        return { ...c, memberTypes: has ? c.memberTypes.filter(t => t !== typeId) : [...(c.memberTypes || []), typeId] };
    }));

    // EAP PROGRAM STATE — the org's assistance-program tie-in (docs/plans/EAP-TIEIN-PLAN.md).
    // `services` is edited as a comma-separated string, stored as an array.
    const EMPTY_EAP = {
        enabled: false, providerName: '', programLabel: 'Employee & Family Assistance Program',
        phone: '', availability: '', crisisPhone: '', ttyPhone: '', internationalPhone: '',
        smsNumber: '', chatUrl: '', appUrl: '',
        url: '', requiresLogin: false, linkParams: '', email: '',
        accessCode: '', accessNote: '', sessionsNote: '', services: '',
        languageNote: '', eligibilityNote: '', familyEligible: true,
        logoUrl: ''
    };
    const [eapData, setEapData] = useState(EMPTY_EAP);

    const [managingOrg, setManagingOrg] = useState(null);
    const [auditingOrg, setAuditingOrg] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // Each entry carries its audience so the dropdowns below can only offer a
    // Youth set to the minor tiers and a non-Youth set to adults - the same
    // rule useAssessmentLogic enforces at load time.
    const [availableAssessments, setAvailableAssessments] = useState(
        Object.keys(ASSESSMENT_LIBRARY).map(k => ({ id: k, title: ASSESSMENT_LIBRARY[k].title, audience: ASSESSMENT_LIBRARY[k].audience }))
    );
    const youthAssessments = availableAssessments.filter(c => c.audience === 'Youth');
    const adultAssessments = availableAssessments.filter(c => c.audience !== 'Youth');

    // Derived state to get the groups of the currently edited org
    const activeOrgGroups = editingOrgId ? (orgs.find(o => o.id === editingOrgId)?.groups || []) : [];

    useEffect(() => {
        const unsubOrgs = onSnapshot(collection(db, "organizations"), (snapshot) => setOrgs(sortOrgs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))));
        
        const unsubConfigs = onSnapshot(collection(db, "assessment_configs"), (snap) => {
            const dbConfigs = snap.docs.map(d => ({ id: d.id, title: d.data().title, audience: d.data().audience }));
            const libConfigs = Object.keys(ASSESSMENT_LIBRARY).map(k => ({ id: k, title: ASSESSMENT_LIBRARY[k].title, audience: ASSESSMENT_LIBRARY[k].audience }));
            
            const configMap = new Map();
            libConfigs.forEach(c => configMap.set(c.id, c));
            dbConfigs.forEach(c => configMap.set(c.id, c));
            setAvailableAssessments(Array.from(configMap.values()));

        }, (error) => {
            console.error("Error fetching configs:", error);
        });

        return () => { unsubOrgs(); unsubConfigs(); };
    }, []);

    const handleLogoUpload = async (orgId) => {
        if (!logoFile) return brandingData.logoUrl;
        const storageRef = ref(storage, `logos/${orgId}/${logoFile.name}`);
        await uploadBytes(storageRef, logoFile);
        return await getDownloadURL(storageRef);
    };

    // The EAP provider's logo. Kept under the SAME logos/{orgId}/ prefix as the
    // org logo (Storage rules aren't in this repo — that prefix is known-writable),
    // distinguished by an eap- filename rather than a new folder.
    const handleEapLogoUpload = async (orgId) => {
        if (!eapLogoFile) return eapData.logoUrl;
        const storageRef = ref(storage, `logos/${orgId}/eap-${Date.now()}-${eapLogoFile.name}`);
        await uploadBytes(storageRef, eapLogoFile);
        return await getDownloadURL(storageRef);
    };

    // Upload any pending contact photos and return the finalized contacts array,
    // dropping empty rows (a contact with no name and no way to reach them).
    const buildSupportContacts = async (orgId) => {
        const out = [];
        for (let i = 0; i < supportContacts.length; i++) {
            const c = supportContacts[i];
            if (!(c.name || c.email || c.phone || c.bookingUrl)) continue;
            let photoUrl = c.photoUrl || '';
            const file = contactPhotoFiles[i];
            if (file) {
                const storageRef = ref(storage, `support-contacts/${orgId}/${Date.now()}-${i}-${file.name}`);
                await uploadBytes(storageRef, file);
                photoUrl = await getDownloadURL(storageRef);
            }
            out.push({
                name: (c.name || '').trim(),
                role: (c.role || '').trim(),
                email: (c.email || '').trim(),
                phone: (c.phone || '').trim(),
                bookingUrl: (c.bookingUrl || '').trim(),
                message: (c.message || '').trim(),
                photoUrl,
                memberTypes: Array.isArray(c.memberTypes) ? c.memberTypes : []
            });
        }
        return out;
    };

    const handleSaveOrg = async (e) => {
        e.preventDefault();
        if (!orgFormData.name) return;

        // Member types: strip the UI-only `enabled` flag; resolveMemberTypes
        // only ever sees the enabled slots. Ids stay fixed — never editable.
        // `surface` is the org's routing choice; the child slot is always
        // age-gated so it saves 'child' regardless.
        const formattedMemberTypes = (orgFormData.memberTypes || [])
            .filter(t => t.enabled)
            .map(({ id, label, expects, surface }) => ({ id, label: (label || '').trim(), expects, surface: id === 'child' ? 'child' : surface }));
        if (formattedMemberTypes.length === 0) {
            alert("Member Types: at least one type must be enabled — new members need something to pick when joining.");
            return;
        }
        if (formattedMemberTypes.some(t => !t.label)) {
            alert("Member Types: every enabled type needs a label.");
            return;
        }

        setIsSaving(true);
        try {
            let targetId = editingOrgId;
            let isNew = false;
            
            if (!targetId) {
                const newDocRef = doc(collection(db, "organizations"));
                targetId = newDocRef.id;
                isNew = true;
            }

            const finalLogoUrl = await handleLogoUpload(targetId);
            const finalEapLogoUrl = await handleEapLogoUpload(targetId);
            const finalSupportContacts = await buildSupportContacts(targetId);

            const formattedCoaches = (orgFormData.coaches || [])
                .filter(c => c.email.trim() && c.groupId)
                .map(c => ({
                    email: c.email.toLowerCase().trim(),
                    groupId: c.groupId
                }));

            const payload = {
                name: orgFormData.name,
                industry: orgFormData.industry, // SAVING INDUSTRY TO DB
                sport: orgFormData.industry === 'sport' ? (orgFormData.sport || '') : '', // data/sports.js; '' = inferred from the name
                edition: orgFormData.edition || DEFAULT_EDITION, // enterprise-console skin
                pathCharacter: orgFormData.pathCharacter || DEFAULT_PATH_CHARACTER,
                supportPathwaysId: orgFormData.supportPathwaysId || '',
                youthSurface: orgFormData.youthSurface || 'age',
                assessmentByMemberType: { spouse: orgFormData.assessmentByMemberType?.spouse || '' },
                aiCoachForMinors: orgFormData.aiCoachForMinors !== false,
                youthContent: { hideMentalHealthUnder13: !!orgFormData.youthContent?.hideMentalHealthUnder13 },
                activeAssessmentId: orgFormData.activeAssessmentId,
                assessmentByTier: {
                    kid: orgFormData.assessmentByTier?.kid || null,
                    teen: orgFormData.assessmentByTier?.teen || null
                },
                cadenceDays: parseInt(orgFormData.cadenceDays) || 30,
                // Position in the super-admin lists (utils/orgOrder.js); blank means after the pinned ones, by name.
                listOrder: Number.isFinite(parseInt(orgFormData.listOrder)) ? parseInt(orgFormData.listOrder) : null,
                // Where the join screen's country and province start (still editable there).
                defaultLocation: { country: (orgFormData.defaultLocation?.country || '').trim(), region: (orgFormData.defaultLocation?.region || '').trim() },
                adminEmails: (orgFormData.adminEmails || '').split(',').map(e=>e.trim()).filter(e=>e),
                coaches: formattedCoaches,
                memberTypes: formattedMemberTypes,
                features: {
                    enableSquadHub: featuresData.enableSquadHub,
                    enableFinanceHub: !!featuresData.enableFinanceHub,
                    enableRecoveryModel: !!featuresData.enableRecoveryModel
                },
                branding: {
                    enabled: brandingData.enabled,
                    // Trimmed: a hex pasted with a leading tab saved as-is, and a
                    // colour with stray whitespace breaks the native colour input.
                    primaryColor: (brandingData.primaryColor || '').trim(),
                    accentColor: (brandingData.accentColor || '').trim(),
                    darkColor: (brandingData.darkColor || '').trim(),
                    sidebarTextColor: (brandingData.sidebarTextColor || '').trim() || '#ffffff',
                    lightColor: (brandingData.lightColor || '').trim(),
                    logoUrl: finalLogoUrl || '',
                    theme: brandingData.theme === 'hub' ? 'hub' : 'classic',
                    hubName: (brandingData.hubName || '').trim(),
                    highlightColor: (brandingData.highlightColor || '').trim()
                },
                supportContacts: finalSupportContacts,
                eapProgram: {
                    enabled: eapData.enabled,
                    providerName: (eapData.providerName || '').trim(),
                    programLabel: (eapData.programLabel || '').trim(),
                    phone: (eapData.phone || '').trim(),
                    availability: (eapData.availability || '').trim(),
                    crisisPhone: (eapData.crisisPhone || '').trim(),
                    ttyPhone: (eapData.ttyPhone || '').trim(),
                    internationalPhone: (eapData.internationalPhone || '').trim(),
                    smsNumber: (eapData.smsNumber || '').trim(),
                    chatUrl: (eapData.chatUrl || '').trim(),
                    appUrl: (eapData.appUrl || '').trim(),
                    url: (eapData.url || '').trim(),
                    requiresLogin: !!eapData.requiresLogin,
                    linkParams: (eapData.linkParams || '').trim(),
                    email: (eapData.email || '').trim(),
                    accessCode: (eapData.accessCode || '').trim(),
                    accessNote: (eapData.accessNote || '').trim(),
                    sessionsNote: (eapData.sessionsNote || '').trim(),
                    services: parseServicesInput(eapData.services),
                    languageNote: (eapData.languageNote || '').trim(),
                    eligibilityNote: (eapData.eligibilityNote || '').trim(),
                    familyEligible: !!eapData.familyEligible,
                    logoUrl: finalEapLogoUrl || ''
                }
            };
            if (isNew) {
                await setDoc(doc(db, "organizations", targetId), { 
                    ...payload, 
                    groups: [], 
                    createdAt: new Date().toISOString() 
                });
            } else {
                await updateDoc(doc(db, "organizations", targetId), payload);
            }

            resetForm();
            alert(isNew ? "Organization Created Successfully!" : "Organization Updated!");

        } catch (e) { 
            console.error(e);
            alert("Error: " + e.message); 
        } finally { 
            setIsSaving(false);
        }
    };

    const resetForm = () => {
        setEditingOrgId(null);
        setShowOrgModal(false);
        setOrgFormData({ name: '', industry: 'sport', sport: '', edition: 'sport', pathCharacter: DEFAULT_PATH_CHARACTER, supportPathwaysId: '', youthSurface: 'age', assessmentByMemberType: { spouse: '' }, aiCoachForMinors: true, youthContent: { hideMentalHealthUnder13: false }, activeAssessmentId: 'core_v1', assessmentByTier: { kid: '', teen: '' }, cadenceDays: 30, listOrder: '', defaultLocation: { country: '', region: '' }, adminEmails: '', coaches: [], memberTypes: defaultMemberTypeSlots() });
        setFeaturesData({ enableSquadHub: false, enableFinanceHub: false, enableRecoveryModel: false });
        setBrandingData({
            enabled: false, primaryColor: '#0679af', accentColor: '#0ea5e9',
            darkColor: '#0f172a', sidebarTextColor: '#ffffff', lightColor: '#f0f9ff', logoUrl: '',
            theme: 'classic', hubName: '', highlightColor: ''
        });
        setLogoFile(null);
        setSupportContacts([]);
        setContactPhotoFiles({});
        setEapData(EMPTY_EAP);
    };

    const handleEditOrgClick = (org) => {
        // Round-trip member types by FIXED id: a saved entry re-enables its slot
        // with its label/expects/surface; a missing entry means the org disabled
        // it. Entries saved before `surface` existed fall back to the slot's
        // default. No memberTypes at all = legacy org -> all three enabled with
        // defaults.
        const savedTypes = Array.isArray(org.memberTypes) ? org.memberTypes : [];
        const memberTypes = DEFAULT_MEMBER_TYPES.map(def => {
            if (savedTypes.length === 0) return { ...def, enabled: true };
            const saved = savedTypes.find(t => t.id === def.id);
            return saved
                ? { id: def.id, label: saved.label || def.label, expects: saved.expects || def.expects, surface: saved.surface || def.surface, enabled: true }
                : { ...def, enabled: false };
        });

        setOrgFormData({
            name: org.name,
            industry: org.industry || 'sport', // FALLBACK TO SPORT FOR LEGACY ORGS
            sport: org.sport || '',
            edition: resolveEditionId(org), // effective edition (explicit or industry generic)
            pathCharacter: resolvePathCharacter(org),
            supportPathwaysId: org.supportPathwaysId || '',
            defaultLocation: { country: org.defaultLocation?.country || '', region: org.defaultLocation?.region || '' },
            youthSurface: org.youthSurface || 'age',
            assessmentByMemberType: { spouse: org.assessmentByMemberType?.spouse || '' },
            aiCoachForMinors: org.aiCoachForMinors !== false,
            youthContent: { hideMentalHealthUnder13: !!org.youthContent?.hideMentalHealthUnder13 },
            activeAssessmentId: org.activeAssessmentId,
            assessmentByTier: { kid: org.assessmentByTier?.kid || '', teen: org.assessmentByTier?.teen || '' },
            cadenceDays: org.cadenceDays,
            listOrder: typeof org.listOrder === 'number' ? org.listOrder : '',
            adminEmails: org.adminEmails ? org.adminEmails.join(', ') : '',
            coaches: org.coaches || [],
            memberTypes
        });

        if (org.features) {
            setFeaturesData({ enableSquadHub: org.features.enableSquadHub || false, enableFinanceHub: org.features.enableFinanceHub === true, enableRecoveryModel: org.features.enableRecoveryModel === true });
        } else {
            setFeaturesData({ enableSquadHub: false, enableFinanceHub: false, enableRecoveryModel: false });
        }

        if (org.branding) {
            setBrandingData({
                enabled: org.branding.enabled || false,
                primaryColor: org.branding.primaryColor || '#0679af',
                accentColor: org.branding.accentColor || '#0ea5e9',
                darkColor: org.branding.darkColor || '#0f172a',
                sidebarTextColor: org.branding.sidebarTextColor || '#ffffff', 
                lightColor: org.branding.lightColor || '#f0f9ff',
                logoUrl: org.branding.logoUrl || '',
                theme: org.branding.theme === 'hub' ? 'hub' : 'classic',
                hubName: org.branding.hubName || '',
                highlightColor: org.branding.highlightColor || ''
            });
        } else {
            setBrandingData({
                enabled: false, primaryColor: '#0679af', accentColor: '#0ea5e9',
                darkColor: '#0f172a', sidebarTextColor: '#ffffff', lightColor: '#f0f9ff', logoUrl: '',
            theme: 'classic', hubName: '', highlightColor: ''
            });
        }

        const normalizeContact = (c) => ({
            name: c.name || '', role: c.role || '', email: c.email || '', phone: c.phone || '',
            bookingUrl: c.bookingUrl || '', message: c.message || '', photoUrl: c.photoUrl || '',
            memberTypes: Array.isArray(c.memberTypes) ? c.memberTypes : []
        });
        if (Array.isArray(org.supportContacts)) {
            setSupportContacts(org.supportContacts.map(normalizeContact));
        } else if (org.supportContact && (org.supportContact.name || org.supportContact.email || org.supportContact.phone || org.supportContact.bookingUrl)) {
            // Migrate a legacy single contact into the list (serves everyone).
            setSupportContacts([normalizeContact(org.supportContact)]);
        } else {
            setSupportContacts([]);
        }

        if (org.eapProgram) {
            setEapData({
                enabled: org.eapProgram.enabled || false,
                providerName: org.eapProgram.providerName || '',
                programLabel: org.eapProgram.programLabel || 'Employee & Family Assistance Program',
                phone: org.eapProgram.phone || '',
                availability: org.eapProgram.availability || '',
                crisisPhone: org.eapProgram.crisisPhone || '',
                ttyPhone: org.eapProgram.ttyPhone || '',
                internationalPhone: org.eapProgram.internationalPhone || '',
                smsNumber: org.eapProgram.smsNumber || '',
                chatUrl: org.eapProgram.chatUrl || '',
                appUrl: org.eapProgram.appUrl || '',
                url: org.eapProgram.url || '',
                requiresLogin: !!org.eapProgram.requiresLogin,
                linkParams: org.eapProgram.linkParams || '',
                email: org.eapProgram.email || '',
                accessCode: org.eapProgram.accessCode || '',
                accessNote: org.eapProgram.accessNote || '',
                sessionsNote: org.eapProgram.sessionsNote || '',
                services: serializeServicesInput(org.eapProgram.services),
                languageNote: org.eapProgram.languageNote || '',
                eligibilityNote: org.eapProgram.eligibilityNote || '',
                familyEligible: org.eapProgram.familyEligible !== false,
                logoUrl: org.eapProgram.logoUrl || ''
            });
        } else {
            setEapData(EMPTY_EAP);
        }

        setLogoFile(null);
        setEapLogoFile(null);
        setContactPhotoFiles({});
        setEditingOrgId(org.id);
        setShowOrgModal(true);
    };

    const handleDeleteOrg = async (id) => { if (!window.confirm("Delete this organization?")) return; try { await deleteDoc(doc(db, "organizations", id));
    } catch (e) { alert(e.message); } };
    const copyInviteLink = (orgId) => { navigator.clipboard.writeText(`${window.location.origin}/#join/${orgId}`); alert("Invite link copied!"); };

    return (
        <div className="relative">
            {managingOrg && <GroupManager org={managingOrg} onClose={() => setManagingOrg(null)} />}
            {auditingOrg && <RosterAudit org={auditingOrg} onClose={() => setAuditingOrg(null)} />} 
            
            {/* Header bar */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-black text-slate-800">Organizations</h2>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{orgs.length} total</p>
                </div>
                <button type="button" onClick={() => { resetForm(); setShowOrgModal(true); }} className="px-4 py-2.5 bg-slate-900 hover:bg-black text-white rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg">
                    <Plus size={16}/> Add Organization
                </button>
            </div>

            {/* --- EDITOR MODAL --- */}
            {showOrgModal && (
                <div className="fixed inset-0 z-[200] flex items-start justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto" onClick={resetForm}>
                    <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl my-6" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="font-black text-slate-800 flex items-center gap-2">{editingOrgId ? <Pencil size={18} className="text-brand-primary"/> : <Plus size={18} className="text-red-500"/>}{editingOrgId ? "Edit Organization" : "Add Organization"}</h3>
                            <button type="button" onClick={resetForm} className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X size={20}/></button>
                        </div>

                    <form onSubmit={handleSaveOrg} className="p-6 space-y-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 gap-2">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Organization Name</label>
                                <input className="w-full p-2 border rounded" value={orgFormData.name} onChange={e=>setOrgFormData({...orgFormData,name:e.target.value})}/>
                            </div>
                            {/* --- NEW: INDUSTRY DROPDOWN --- */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1 mt-1">Industry Sector</label>
                                <select className="w-full p-2 border rounded text-xs" value={orgFormData.industry} onChange={e=>{
                                    const industry = e.target.value;
                                    // A generic edition follows the industry (a community hub uses the
                                    // generic corporate console); an explicit vertical (insurance…) sticks.
                                    const isGeneric = orgFormData.edition === 'sport' || orgFormData.edition === 'corporate' || orgFormData.edition === 'community';
                                    const generic = { sport: 'sport', corporate: 'corporate', community: 'community', responder: 'firstresponder' };
                                    const wasGeneric = isGeneric || orgFormData.edition === 'firstresponder';
                                    setOrgFormData({...orgFormData, industry, edition: wasGeneric ? (generic[industry] || 'corporate') : orgFormData.edition});
                                }}>
                                    <option value="sport">Sport & Athletics (Coach/Athlete)</option>
                                    <option value="corporate">Corporate & Enterprise (Manager/Employee)</option>
                                    <option value="community">Community & Services (Member/Family)</option>
                                    <option value="responder">First Responders (Member/Family)</option>
                                </select>
                                {orgFormData.industry === 'community' && (
                                    <p className="text-[10px] text-slate-400 mt-1">A community wellness hub: the AI Coach uses everyday settings (no sport or office examples), the console uses the Community edition (read by community, no leave forecast or ROI), and the base content library applies.</p>
                                )}
                                {orgFormData.industry === 'responder' && (
                                    <p className="text-[10px] text-slate-400 mt-1">Police, fire, paramedic, dispatch, corrections: the AI Coach speaks in shifts and calls and coaches like a performance coach, and the console uses the First Responders edition (units, strain, operational availability).</p>
                                )}
                                {orgFormData.industry === 'sport' && (
                                    <div className="mt-2">
                                        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Sport</label>
                                        <select className="w-full p-2 border rounded text-xs" value={orgFormData.sport || ''} onChange={e => setOrgFormData({ ...orgFormData, sport: e.target.value })}>
                                            <option value="">Infer from the name ({(inferSportFromName(orgFormData.name) || DEFAULT_SPORT)})</option>
                                            {SPORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                        </select>
                                        <p className="text-[10px] text-slate-400 mt-1">Sets the words the AI Coach, the parent check-in and the talk guides use (ice, field, hill...). Keys never change.</p>
                                    </div>
                                )}
                            </div>
                            {/* --- EDITION (ENTERPRISE CONSOLE SKIN) --- */}
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase mb-1 mt-1">Console Edition</label>
                                <select className="w-full p-2 border rounded text-xs" value={orgFormData.edition} onChange={e=>setOrgFormData({...orgFormData,edition:e.target.value})}>
                                    {Object.values(EDITIONS).map(ed => (
                                        <option key={ed.id} value={ed.id}>{ed.label}</option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-slate-400 mt-1">Drives the executive console's taxonomy, copy, funnel, ROI benchmarks and palette for this client's vertical.</p>
                            </div>
                        </div>
                        
                        {/* Assessment Config */}
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">Adult Assessment</label><select className="w-full p-2 border rounded text-xs" value={orgFormData.activeAssessmentId} onChange={e=>setOrgFormData({...orgFormData,activeAssessmentId:e.target.value})}>{adultAssessments.map(c=><option key={c.id} value={c.id}>{c.title}</option>)}</select><p className="text-[10px] text-slate-400 mt-1">Adults only. A Youth set is never served to an adult, whatever is chosen here.</p></div>
                            <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">Cadence (Days)</label><input type="number" className="w-full p-2 border rounded text-xs" value={orgFormData.cadenceDays} onChange={e=>setOrgFormData({...orgFormData,cadenceDays:e.target.value})}/></div>
                            <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">List order</label><input type="number" min="1" placeholder="blank = by name" className="w-full p-2 border rounded text-xs" value={orgFormData.listOrder ?? ''} onChange={e=>setOrgFormData({...orgFormData,listOrder:e.target.value})}/></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">Default country</label><input className="w-full p-2 border rounded text-xs" placeholder="e.g. Canada" value={orgFormData.defaultLocation?.country || ''} onChange={e=>setOrgFormData({...orgFormData, defaultLocation: { ...(orgFormData.defaultLocation || {}), country: e.target.value }})}/></div>
                            <div><label className="block text-xs font-bold text-slate-400 uppercase mb-1">Default province / state</label><input className="w-full p-2 border rounded text-xs" placeholder="e.g. Alberta" value={orgFormData.defaultLocation?.region || ''} onChange={e=>setOrgFormData({...orgFormData, defaultLocation: { ...(orgFormData.defaultLocation || {}), region: e.target.value }})}/></div>
                            <p className="col-span-2 text-[10px] text-slate-400 leading-tight">Where the join screen's location starts for this organization's members. Spelled as the join screen lists them; members can still change it.</p>
                        </div>

                        {/* Per-age-tier assessment overrides. Blank = safe age default (Youth Check-In). */}
                        <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded">
                            <p className="text-[10px] text-slate-500 leading-tight mb-2">Minors are routed to an age-appropriate assessment regardless of the adult default. Leave blank to use the safe default (Youth Check-In).</p>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Kid (&lt;16) Assessment</label>
                                    <select className="w-full p-2 border rounded text-xs" value={orgFormData.assessmentByTier?.kid || ''} onChange={e=>setOrgFormData({...orgFormData, assessmentByTier:{...orgFormData.assessmentByTier, kid:e.target.value}})}>
                                        <option value="">Default — Youth Check-In</option>
                                        {youthAssessments.map(c=><option key={c.id} value={c.id}>{c.title}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Teen (13–17) Assessment</label>
                                    <select className="w-full p-2 border rounded text-xs" value={orgFormData.assessmentByTier?.teen || ''} onChange={e=>setOrgFormData({...orgFormData, assessmentByTier:{...orgFormData.assessmentByTier, teen:e.target.value}})}>
                                        <option value="">Default — Youth Check-In</option>
                                        {youthAssessments.map(c=><option key={c.id} value={c.id}>{c.title}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* --- MEMBER TYPES (JOIN FLOW PERSONAS) --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase mb-1">
                                <Users size={14} className="text-emerald-500"/> Member Types
                            </label>
                            <p className="text-[10px] text-slate-500 leading-tight mb-2">These are the options a new member picks when joining. The label is what members see; "Routes to" is the experience they get. A spouse in a corporate org is usually just another member (Adult member surface); for alumni, the Family / caregiver hub fits. Ids are fixed — they stay the stable tag for segmentation.</p>
                            <div className="space-y-2 bg-slate-50 p-3 rounded border border-slate-200">
                                {orgFormData.memberTypes.map((t, idx) => (
                                    <div key={t.id}>
                                        <div className="flex gap-2 items-center">
                                            <input
                                                type="checkbox"
                                                className="shrink-0 cursor-pointer"
                                                checked={t.enabled}
                                                title="Enable this member type for the join flow"
                                                onChange={e => { const newT = [...orgFormData.memberTypes]; newT[idx] = { ...newT[idx], enabled: e.target.checked }; setOrgFormData({...orgFormData, memberTypes: newT}); }}
                                            />
                                            <input
                                                className="flex-1 p-2 border rounded text-xs outline-none focus:border-brand-primary disabled:bg-slate-100 disabled:text-slate-400"
                                                placeholder="Label shown to new members"
                                                value={t.label}
                                                disabled={!t.enabled}
                                                onChange={e => { const newT = [...orgFormData.memberTypes]; newT[idx] = { ...newT[idx], label: e.target.value }; setOrgFormData({...orgFormData, memberTypes: newT}); }}
                                            />
                                            <select
                                                className="w-1/4 p-2 border rounded text-xs outline-none focus:border-brand-primary disabled:bg-slate-100 disabled:text-slate-400"
                                                value={t.expects}
                                                disabled={!t.enabled}
                                                onChange={e => { const newT = [...orgFormData.memberTypes]; newT[idx] = { ...newT[idx], expects: e.target.value }; setOrgFormData({...orgFormData, memberTypes: newT}); }}
                                            >
                                                <option value="adult">Adult</option>
                                                <option value="teen">Teen</option>
                                                <option value="kid">Kid</option>
                                                <option value="any">Any</option>
                                            </select>
                                        </div>
                                        <div className="flex gap-2 items-center mt-0.5 ml-6">
                                            <span className="text-[10px] text-slate-400 shrink-0"><span className="font-mono">{t.id}</span> routes to &rarr;</span>
                                            {t.id === 'child' ? (
                                                <span className="text-[10px] text-slate-400">Age-gated (kids / teen)</span>
                                            ) : (
                                                <select
                                                    className="p-1 border rounded text-[10px] outline-none focus:border-brand-primary disabled:bg-slate-100 disabled:text-slate-400"
                                                    title="Routes to — the surface this member type lands on"
                                                    value={t.surface}
                                                    disabled={!t.enabled}
                                                    onChange={e => { const newT = [...orgFormData.memberTypes]; newT[idx] = { ...newT[idx], surface: e.target.value }; setOrgFormData({...orgFormData, memberTypes: newT}); }}
                                                >
                                                    {ADULT_SURFACE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* --- ROLE ASSIGNMENTS --- */}
                        <div className="pt-2">
                            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Org Admins (Comma Sep)</label>
                            <textarea className="w-full p-2 border rounded h-12 text-xs" placeholder="admin@team.com" value={orgFormData.adminEmails} onChange={e=>setOrgFormData({...orgFormData,adminEmails:e.target.value})}/>
                        </div>

                        {/* --- PLATFORM FEATURES --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase">
                                    <Users size={14} className="text-blue-500"/> Squad Management
                                </label>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                    <input type="checkbox" name="toggle-squad" id="toggle-squad" className="toggle-checkbox toggle-blue absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer" checked={featuresData.enableSquadHub} onChange={e => setFeaturesData({...featuresData, enableSquadHub: e.target.checked})}/>
                                    <label htmlFor="toggle-squad" className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${featuresData.enableSquadHub ? 'bg-blue-500' : 'bg-slate-300'}`}></label>
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-tight mb-3">Enables Coach/Manager role. Allows users to select sub-teams during onboarding and unlocks the Manager Playbook.</p>
                            
                            {/* --- DYNAMIC COACH ASSIGNER --- */}
                            {featuresData.enableSquadHub && (
                                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 mt-3 space-y-3 animate-in fade-in">
                                    <label className="block text-xs font-bold text-slate-700 uppercase">Assign Sub-Group Leaders</label>
                                    
                                    {orgFormData.coaches.map((c, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <input 
                                                className="flex-1 p-2 border border-slate-200 rounded text-xs outline-none focus:border-blue-400" 
                                                placeholder="leader@team.com" 
                                                value={c.email} 
                                                onChange={e => { const newC = [...orgFormData.coaches]; newC[idx].email = e.target.value; setOrgFormData({...orgFormData, coaches: newC}); }} 
                                            />
                                            <select 
                                                className="w-1/3 p-2 border border-slate-200 rounded text-xs outline-none focus:border-blue-400" 
                                                value={c.groupId} 
                                                onChange={e => { const newC = [...orgFormData.coaches]; newC[idx].groupId = e.target.value; setOrgFormData({...orgFormData, coaches: newC}); }}
                                            >
                                                <option value="" disabled>Select Group</option>
                                                {activeOrgGroups.map(g => <option key={g} value={g}>{g}</option>)}
                                            </select>
                                            <button 
                                                type="button" 
                                                onClick={() => { const newC = orgFormData.coaches.filter((_, i) => i !== idx); setOrgFormData({...orgFormData, coaches: newC}); }} 
                                                className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                                            >
                                                <Trash2 size={16}/>
                                            </button>
                                        </div>
                                    ))}

                                    {activeOrgGroups.length > 0 ? (
                                        <button 
                                            type="button" 
                                            onClick={() => setOrgFormData({...orgFormData, coaches: [...orgFormData.coaches, {email: '', groupId: ''}]})} 
                                            className="text-xs text-blue-600 font-bold hover:underline flex items-center gap-1 mt-2"
                                        >
                                            <Plus size={12}/> Add Leader
                                        </button>
                                    ) : (
                                        <p className="text-[10px] text-blue-600 italic">Save organization and create Teams (Sub-groups) first to assign leaders.</p>
                                    )}
                                </div>
                            )}

                            {/* --- STAFF SIGN-UP CODE --- */}
                            {featuresData.enableSquadHub && editingOrgId && (
                                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-3 animate-in fade-in">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <label className="block text-xs font-bold text-slate-700 uppercase">Staff sign-up code</label>
                                            <p className="text-[10px] text-slate-500 leading-tight mt-1">A team's join link plus <span className="font-mono">/staff/CODE</span> lets bench staff (assistants, the manager, a division director) join onto that team's coaches roster and get the team view. The roster import prints one per team; a coach finds theirs on the team dashboard. Rotate it if it leaks: staff who have joined keep their access.</p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-mono font-black text-slate-900 text-sm tracking-widest">{staffCode || 'none yet'}</p>
                                            <button type="button" onClick={rotateStaffCode} className="text-xs text-blue-600 font-bold hover:underline mt-1">{staffCode ? 'Rotate' : 'Generate'}</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* --- ALBERTA RECOVERY MODEL TAB (executive console) --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase">
                                    <Landmark size={14} className="text-indigo-500"/> Alberta Recovery Model
                                </label>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                    <input type="checkbox" name="toggle-recovery" id="toggle-recovery" className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer" checked={!!featuresData.enableRecoveryModel} onChange={e => setFeaturesData({...featuresData, enableRecoveryModel: e.target.checked})}/>
                                    <label htmlFor="toggle-recovery" className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${featuresData.enableRecoveryModel ? 'bg-indigo-500' : 'bg-slate-300'}`}></label>
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-tight mb-3">Alberta Recovery Model tab in the executive console: the console's figures regrouped under the continuum, the five key focus areas, the ministry's 2025-28 outcomes and the five key principles. For a ministry, a police service, a school division or a public program. Off by default.</p>
                        </div>

                        {/* --- BRANDING SECTION (PREMIUM) --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between mb-3">
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase">
                                    <Palette size={14} className="text-purple-500"/> Branding (Premium)
                                </label>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                    <input type="checkbox" name="toggle" id="toggle" className="toggle-checkbox toggle-purple absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer" checked={brandingData.enabled} onChange={e => setBrandingData({...brandingData, enabled: e.target.checked})}/>
                                    <label htmlFor="toggle" className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${brandingData.enabled ? 'bg-purple-500' : 'bg-slate-300'}`}></label>
                                </div>
                            </div>

                            {brandingData.enabled && (
                                <div className="space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-100 animate-in fade-in">

                                    {/* THEME - classic coloured sidebar, or the white-label Hub look */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Theme</label>
                                        <select className="w-full p-2 border rounded text-xs" value={brandingData.theme || 'classic'} onChange={e => setBrandingData({...brandingData, theme: e.target.value})}>
                                            {ORG_THEMES.map(th => <option key={th.id} value={th.id}>{th.label}</option>)}
                                        </select>
                                    </div>
                                    {brandingData.theme === 'hub' && (
                                        <div className="grid grid-cols-2 gap-3 animate-in fade-in">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Hub Name</label>
                                                <input className="w-full p-2 border rounded text-xs" placeholder="e.g. Hockey Alberta Hub" value={brandingData.hubName} onChange={e => setBrandingData({...brandingData, hubName: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Highlight Surface</label>
                                                <div className="flex gap-2 items-center">
                                                    <input type="color" className="h-8 w-8 p-0 border-0 rounded cursor-pointer shrink-0" value={brandingData.highlightColor || '#fcf6e9'} onChange={e => setBrandingData({...brandingData, highlightColor: e.target.value})} />
                                                    <input type="text" className="w-full p-1 text-xs border rounded uppercase font-mono" placeholder="#FCF6E9" value={brandingData.highlightColor} onChange={e => setBrandingData({...brandingData, highlightColor: e.target.value})} />
                                                </div>
                                            </div>
                                            <p className="col-span-2 text-[10px] text-slate-400 leading-tight">Hub shows the client's own product name with a "Powered by EAS" credit, a light sidebar with the Primary colour on the active item and the Accent colour on its icon, and flat Primary heroes with an Accent bar. Sidebar and Sidebar Text colours below are ignored in this theme.</p>
                                        </div>
                                    )}
                                    
                                    {/* COLOR PICKERS GRID */}
                                    <div className="grid grid-cols-2 gap-3">
                                        
                                        {/* Primary */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Primary (Brand)</label>
                                            <div className="flex gap-2 items-center">
                                                <input type="color" className="h-8 w-8 p-0 border-0 rounded cursor-pointer shrink-0" value={brandingData.primaryColor} onChange={e => setBrandingData({...brandingData, primaryColor: e.target.value})} />
                                                <input type="text" className="w-full p-1 text-xs border rounded uppercase font-mono" value={brandingData.primaryColor} onChange={e => setBrandingData({...brandingData, primaryColor: e.target.value})} />
                                            </div>
                                        </div>

                                        {/* Accent */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Accent (Action)</label>
                                            <div className="flex gap-2 items-center">
                                                <input type="color" className="h-8 w-8 p-0 border-0 rounded cursor-pointer shrink-0" value={brandingData.accentColor} onChange={e => setBrandingData({...brandingData, accentColor: e.target.value})} />
                                                <input type="text" className="w-full p-1 text-xs border rounded uppercase font-mono" value={brandingData.accentColor} onChange={e => setBrandingData({...brandingData, accentColor: e.target.value})} />
                                            </div>
                                        </div>

                                        {/* Dark (Sidebar Background) */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Sidebar (Bg)</label>
                                            <div className="flex gap-2 items-center">
                                                <input type="color" className="h-8 w-8 p-0 border-0 rounded cursor-pointer shrink-0" value={brandingData.darkColor} onChange={e => setBrandingData({...brandingData, darkColor: e.target.value})} />
                                                <input type="text" className="w-full p-1 text-xs border rounded uppercase font-mono" value={brandingData.darkColor} onChange={e => setBrandingData({...brandingData, darkColor: e.target.value})} />
                                            </div>
                                        </div>

                                        {/* Sidebar Text */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Sidebar (Text)</label>
                                            <div className="flex gap-2 items-center">
                                                <input type="color" className="h-8 w-8 p-0 border-0 rounded cursor-pointer shrink-0" value={brandingData.sidebarTextColor} onChange={e => setBrandingData({...brandingData, sidebarTextColor: e.target.value})} />
                                                <input type="text" className="w-full p-1 text-xs border rounded uppercase font-mono" value={brandingData.sidebarTextColor} onChange={e => setBrandingData({...brandingData, sidebarTextColor: e.target.value})} />
                                            </div>
                                        </div>

                                        {/* Light (Bg) */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Background (Light)</label>
                                            <div className="flex gap-2 items-center">
                                                <input type="color" className="h-8 w-8 p-0 border-0 rounded cursor-pointer shrink-0" value={brandingData.lightColor} onChange={e => setBrandingData({...brandingData, lightColor: e.target.value})} />
                                                <input type="text" className="w-full p-1 text-xs border rounded uppercase font-mono" value={brandingData.lightColor} onChange={e => setBrandingData({...brandingData, lightColor: e.target.value})} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Logo Upload */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Logo (PNG, JPG, SVG)</label>
                                        {brandingData.logoUrl && !logoFile && (
                                            <div className="mb-2 p-2 bg-white border rounded flex items-center gap-2">
                                                <img src={brandingData.logoUrl} alt="Preview" className="h-6 w-6 object-contain"/>
                                                <span className="text-[10px] text-green-600 font-bold truncate">Current Logo Active</span>
                                            </div>
                                        )}
                                        <div className="relative">
                                            <input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0])} className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"/>
                                            <Upload size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* --- SUPPORT CONTACTS SECTION (PERSONAL CONNECTION OUTLET) --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase">
                                    <HeartHandshake size={14} className="text-rose-500"/> Support Contacts
                                </label>
                                <button type="button" onClick={addSupportContact} className="text-[10px] font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 px-2 py-1 rounded flex items-center gap-1">
                                    <Plus size={12}/> Add Contact
                                </button>
                            </div>
                            <p className="text-[10px] text-slate-400 mb-3">Real people members reach when they tap "Talk to Someone". Tag each with who they're for — e.g. Former Players reach one person, Spouse &amp; Son/Daughter reach another. Leave all unchecked to serve everyone.</p>

                            {supportContacts.length === 0 && (
                                <p className="text-[10px] text-slate-400 italic bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3 text-center">No support contact yet. Add one to give members a personal connection outlet.</p>
                            )}

                            <div className="space-y-3">
                                {supportContacts.map((c, idx) => {
                                    const enabledTypes = (orgFormData.memberTypes || []).filter(t => t.enabled);
                                    const isDefault = !c.memberTypes || c.memberTypes.length === 0;
                                    return (
                                        <div key={idx} className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-2 relative">
                                            <button type="button" onClick={() => removeSupportContact(idx)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500" title="Remove contact"><X size={14}/></button>

                                            <div className="grid grid-cols-1 gap-2 pr-5">
                                                <input className="w-full p-2 border rounded text-xs" placeholder="Name (e.g. Jane Doe)" value={c.name} onChange={e => updateSupportContact(idx, { name: e.target.value })}/>
                                                <input className="w-full p-2 border rounded text-xs" placeholder="Role / Title (e.g. Alumni Wellness Lead)" value={c.role} onChange={e => updateSupportContact(idx, { role: e.target.value })}/>
                                            </div>

                                            <div className="grid grid-cols-1 gap-2">
                                                <input type="email" className="w-full p-2 border rounded text-xs" placeholder="Email" value={c.email} onChange={e => updateSupportContact(idx, { email: e.target.value })}/>
                                                <input type="tel" className="w-full p-2 border rounded text-xs" placeholder="Phone / Text" value={c.phone} onChange={e => updateSupportContact(idx, { phone: e.target.value })}/>
                                                <input type="url" className="w-full p-2 border rounded text-xs" placeholder="Booking link (Calendly, Teams…)" value={c.bookingUrl} onChange={e => updateSupportContact(idx, { bookingUrl: e.target.value })}/>
                                            </div>

                                            <textarea rows={2} className="w-full p-2 border rounded text-xs resize-none" placeholder="Personal message (optional)" value={c.message} onChange={e => updateSupportContact(idx, { message: e.target.value })}/>

                                            <div>
                                                {c.photoUrl && !contactPhotoFiles[idx] && (
                                                    <div className="mb-1 flex items-center gap-2">
                                                        <img src={c.photoUrl} alt="Contact" className="h-6 w-6 object-cover rounded-full"/>
                                                        <span className="text-[10px] text-green-600 font-bold">Current photo</span>
                                                    </div>
                                                )}
                                                <input type="file" accept="image/*" onChange={e => setContactPhotoFiles(prev => ({ ...prev, [idx]: e.target.files[0] }))} className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100"/>
                                            </div>

                                            <div>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Who is this for?</p>
                                                {enabledTypes.length === 0 ? (
                                                    <p className="text-[10px] text-slate-400 italic">Enable Member Types above to target contacts. For now this contact serves everyone.</p>
                                                ) : (
                                                    <div className="flex flex-wrap gap-2">
                                                        {enabledTypes.map(t => {
                                                            const checked = (c.memberTypes || []).includes(t.id);
                                                            return (
                                                                <button type="button" key={t.id} onClick={() => toggleContactMemberType(idx, t.id)} className={`text-[10px] font-bold px-2 py-1 rounded-full border transition-colors ${checked ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-slate-500 border-slate-200 hover:border-rose-300'}`}>
                                                                    {t.label || t.id}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {isDefault && enabledTypes.length > 0 && (
                                                    <p className="text-[10px] text-slate-400 italic mt-1">None selected → default for anyone not covered by another contact.</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* --- PARENT / PARTNER CHECK-IN (the second adult slot) --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase mb-1">
                                <Users size={14} className="text-emerald-500"/> Parent / Partner Check-In
                            </label>
                            <select
                                className="w-full p-2 border rounded text-xs"
                                value={orgFormData.assessmentByMemberType?.spouse || ''}
                                onChange={e => setOrgFormData({ ...orgFormData, assessmentByMemberType: { ...(orgFormData.assessmentByMemberType || {}), spouse: e.target.value } })}
                            >
                                <option value="">Same as adults</option>
                                {adultAssessments.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                            </select>
                            <p className="text-[10px] text-slate-500 leading-tight mt-1">The check-in for the second adult member type (Parent / Spouse). On a youth-sport org, "Parent Check-In" asks about their player instead of about themselves and routes them to the talk guides. Pair it with the Parent hub in Member types.</p>
                        </div>

                        {/* --- GUIDED PATH CHARACTER --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase mb-1">
                                <Footprints size={14} className="text-indigo-500"/> Guided Path Character
                            </label>
                            <select
                                className="w-full p-2 border rounded text-xs"
                                value={orgFormData.pathCharacter || DEFAULT_PATH_CHARACTER}
                                onChange={e => setOrgFormData({ ...orgFormData, pathCharacter: e.target.value })}
                            >
                                {PATH_CHARACTER_OPTIONS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                            <p className="text-[10px] text-slate-500 leading-tight mt-1">The figure that travels the Guided Path on a member's dashboard. It wears the organization's Primary and Accent colours.</p>
                        </div>

                        {/* --- HELP & SUPPORT PATHWAYS --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase mb-1">
                                <HeartHandshake size={14} className="text-rose-500"/> Help & Support Pathways
                            </label>
                            <select
                                className="w-full p-2 border rounded text-xs"
                                value={orgFormData.supportPathwaysId || ''}
                                onChange={e => setOrgFormData({ ...orgFormData, supportPathwaysId: e.target.value })}
                            >
                                {SUPPORT_PATHWAY_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                            <p className="text-[10px] text-slate-500 leading-tight mt-1">Adds "talk to someone you trust" guidance and conversation starters to Talk to Someone, and a "a player asked to talk" block on the coach dashboard. Sets are data modules (src/data/supportPathways.js).</p>
                        </div>

                        {/* --- MEMBERS UNDER 18 --- */}
                        <div className="pt-4 border-t border-slate-100 space-y-2">
                            <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase mb-1">
                                <Users size={14} className="text-emerald-500"/> Members Under 18
                            </label>
                            <select
                                className="w-full p-2 border rounded text-xs"
                                value={orgFormData.youthSurface || 'age'}
                                onChange={e => setOrgFormData({ ...orgFormData, youthSurface: e.target.value })}
                            >
                                {YOUTH_SURFACE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                            <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                <input type="checkbox" checked={orgFormData.aiCoachForMinors !== false} onChange={e => setOrgFormData({ ...orgFormData, aiCoachForMinors: e.target.checked })} />
                                AI Coach available to members under 18
                            </label>
                            <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                <input type="checkbox" checked={!!orgFormData.youthContent?.hideMentalHealthUnder13} onChange={e => setOrgFormData({ ...orgFormData, youthContent: { ...(orgFormData.youthContent || {}), hideMentalHealthUnder13: e.target.checked } })} />
                                Hide the Mental Health section for members under 13
                            </label>
                            <p className="text-[10px] text-slate-500 leading-tight">Whatever is chosen, members under 18 never see Addictions, Gambling, Eating Disorders, PTSD &amp; Trauma, Leadership, Career or Transition, and under-13s never see Financial Wellbeing. Consent, youth crisis lines and the youth check-in apply under 18 regardless.</p>
                        </div>

                        {/* --- EAP / ASSISTANCE PROGRAM (BENEFITS TIE-IN) --- */}
                        {/* --- FINANCIAL WELLBEING (partner content) --- */}
                        <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase">
                                    <Wallet size={14} className="text-emerald-500"/> Financial Wellbeing
                                </label>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                    <input type="checkbox" name="toggle-finance" id="toggle-finance" className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer" checked={!!featuresData.enableFinanceHub} onChange={e => setFeaturesData({...featuresData, enableFinanceHub: e.target.checked})}/>
                                    <label htmlFor="toggle-finance" className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${featuresData.enableFinanceHub ? 'bg-emerald-500' : 'bg-slate-300'}`}></label>
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-tight mb-3">Adds the Scotia-powered money library: a Financial Wellbeing tab for adults, a Money section on the teen hub, and Money Smarts in Kidz Corner. Content is age-tiered per item in Content Library &rarr; Financial Wellbeing. Off by default until the library has content.</p>
                        </div>

                        <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                                <label className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase">
                                    <LifeBuoy size={14} className="text-teal-500"/> EAP / Assistance Program
                                </label>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                    <input type="checkbox" name="toggle-eap" id="toggle-eap" className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer" checked={eapData.enabled} onChange={e => setEapData({...eapData, enabled: e.target.checked})}/>
                                    <label htmlFor="toggle-eap" className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${eapData.enabled ? 'bg-teal-500' : 'bg-slate-300'}`}></label>
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-400 mb-3">Routes members into the plan's actual service providers from every "Talk to Someone" surface. The 24/7 line doubles as the adult crisis fallback.</p>

                            {eapData.enabled && (
                                <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-100 animate-in fade-in">
                                    <div className="grid grid-cols-1 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Program Label (what members see)</label>
                                            <input className="w-full p-2 border rounded text-xs" placeholder="Employee & Family Assistance Program" value={eapData.programLabel} onChange={e => setEapData({...eapData, programLabel: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Provider</label>
                                            <input className="w-full p-2 border rounded text-xs" placeholder="e.g. TELUS Health, Homewood Health…" value={eapData.providerName} onChange={e => setEapData({...eapData, providerName: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Provider Logo (PNG, JPG, SVG)</label>
                                            {eapData.logoUrl && !eapLogoFile && (
                                                <div className="mb-2 p-2 bg-white border rounded flex items-center gap-2">
                                                    <img src={eapData.logoUrl} alt="Provider logo preview" className="h-6 max-w-[90px] object-contain shrink-0"/>
                                                    <span className="text-[10px] text-green-600 font-bold truncate">Current provider logo active</span>
                                                    <button type="button" onClick={() => setEapData({...eapData, logoUrl: ''})} className="ml-auto text-[10px] font-bold text-red-500 hover:text-red-700 shrink-0">Remove</button>
                                                </div>
                                            )}
                                            <div className="relative">
                                                <input type="file" accept="image/*" onChange={e => setEapLogoFile(e.target.files[0])} className="w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"/>
                                                <Upload size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-1">Shown on the member's dashboard before they click, and above the phone and portal buttons. A wide wordmark on a transparent background works best.</p>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Intake Phone</label>
                                            <input type="tel" className="w-full p-2 border rounded text-xs" placeholder="1-800-…" value={eapData.phone} onChange={e => setEapData({...eapData, phone: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Availability</label>
                                            <input className="w-full p-2 border rounded text-xs" placeholder="24/7  ·  or  ·  Mon–Fri 8am–8pm ET" value={eapData.availability} onChange={e => setEapData({...eapData, availability: e.target.value})}/>
                                            <p className="text-[10px] text-slate-400 mt-1">Shown beside the number. Leave blank when the provider has not stated one — members then see nothing rather than an assumed 24/7.</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Crisis / After-Hours</label>
                                                <input type="tel" className="w-full p-2 border rounded text-xs" placeholder="If separate from intake" value={eapData.crisisPhone} onChange={e => setEapData({...eapData, crisisPhone: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Text / SMS Intake</label>
                                                <input type="tel" className="w-full p-2 border rounded text-xs" placeholder="Optional" value={eapData.smsNumber} onChange={e => setEapData({...eapData, smsNumber: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">TTY Line</label>
                                                <input type="tel" className="w-full p-2 border rounded text-xs" placeholder="Accessibility" value={eapData.ttyPhone} onChange={e => setEapData({...eapData, ttyPhone: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">International / Collect</label>
                                                <input type="tel" className="w-full p-2 border rounded text-xs" placeholder="For members abroad" value={eapData.internationalPhone} onChange={e => setEapData({...eapData, internationalPhone: e.target.value})}/>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Member Portal URL</label>
                                            <input type="url" className="w-full p-2 border rounded text-xs" placeholder="https://…" value={eapData.url} onChange={e => setEapData({...eapData, url: e.target.value})}/>
                                            <label className="flex items-center gap-2 mt-1.5 cursor-pointer">
                                                <input type="checkbox" checked={eapData.requiresLogin} onChange={e => setEapData({...eapData, requiresLogin: e.target.checked})}/>
                                                <span className="text-[11px] font-bold text-slate-600">Portal requires sign-in</span>
                                                <span className="text-[10px] text-slate-400">— warns members before they tap</span>
                                            </label>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Live Chat URL</label>
                                                <input type="url" className="w-full p-2 border rounded text-xs" placeholder="Optional" value={eapData.chatUrl} onChange={e => setEapData({...eapData, chatUrl: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Provider App Link</label>
                                                <input type="url" className="w-full p-2 border rounded text-xs" placeholder="App store or deep link" value={eapData.appUrl} onChange={e => setEapData({...eapData, appUrl: e.target.value})}/>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Link Tracking Parameters</label>
                                            <input className="w-full p-2 border rounded text-xs font-mono" placeholder="utm_source=erihub&amp;partner=EAS" value={eapData.linkParams} onChange={e => setEapData({...eapData, linkParams: e.target.value})}/>
                                            <p className="text-[10px] text-slate-400 mt-1">Appended to every outbound provider link. Paste exactly what the provider supplies — it lets them attribute referrals back to us.</p>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Intake Email (optional)</label>
                                            <input type="email" className="w-full p-2 border rounded text-xs" placeholder="intake@provider.com" value={eapData.email} onChange={e => setEapData({...eapData, email: e.target.value})}/>
                                            <p className="text-[10px] text-slate-400 mt-1">Shown beside the phone and portal, and included in the contact card members save to their phone.</p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Employer / Group Access Code</label>
                                        <input className="w-full p-2 border rounded text-xs font-mono" placeholder="e.g. ACME2026" value={eapData.accessCode} onChange={e => setEapData({...eapData, accessCode: e.target.value})}/>
                                        <p className="text-[10px] text-slate-400 mt-1">Shown to members in its own highlighted, tap-to-copy row and included in the contact card they save. Not knowing this code is the most common reason an intake call fails.</p>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Access Note</label>
                                        <textarea rows={2} className="w-full p-2 border rounded text-xs resize-none" placeholder='e.g. "Free and confidential. Mention you are an [Org] plan member."' value={eapData.accessNote} onChange={e => setEapData({...eapData, accessNote: e.target.value})}/>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Sessions Note (optional)</label>
                                            <input className="w-full p-2 border rounded text-xs" placeholder="e.g. Up to 6 sessions per issue, per year" value={eapData.sessionsNote} onChange={e => setEapData({...eapData, sessionsNote: e.target.value})}/>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Services — one per line</label>
                                            <textarea
                                                rows={4}
                                                className="w-full p-2 border rounded text-xs resize-none font-mono"
                                                placeholder={'Counselling | https://provider.com/book | Choose "Book a session" after sign-in\nLegal advice | https://provider.com/legal\nFinancial advice'}
                                                value={eapData.services}
                                                onChange={e => setEapData({...eapData, services: e.target.value})}
                                            />
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                <span className="font-mono">Label | link | hint</span> — link and hint optional. Providers route counselling, legal and financial to different intakes, so a deep link per service beats one portal URL. The hint shows under the label for portals that cannot deep-link past their login.
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Who is Covered</label>
                                                <input className="w-full p-2 border rounded text-xs" placeholder="Employees, spouses, dependents to 21" value={eapData.eligibilityNote} onChange={e => setEapData({...eapData, eligibilityNote: e.target.value})}/>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Languages</label>
                                                <input className="w-full p-2 border rounded text-xs" placeholder="Service in 150+ languages" value={eapData.languageNote} onChange={e => setEapData({...eapData, languageNote: e.target.value})}/>
                                            </div>
                                        </div>
                                    </div>
                                    <label className="flex items-center gap-2 pt-1 cursor-pointer">
                                        <input type="checkbox" checked={eapData.familyEligible} onChange={e => setEapData({...eapData, familyEligible: e.target.checked})}/>
                                        <span className="text-xs font-bold text-slate-600">Family eligible</span>
                                        <span className="text-[10px] text-slate-400">— spouses &amp; dependents see it on their surfaces too</span>
                                    </label>
                                </div>
                            )}
                        </div>

                        <button disabled={isSaving} className={`w-full py-3 text-white rounded-xl font-bold text-sm shadow-lg flex items-center justify-center gap-2 ${editingOrgId ? 'bg-brand-primary hover:bg-blue-600' : 'bg-slate-900 hover:bg-black'}`}>
                            {isSaving ? <Loader2 className="animate-spin" size={16}/> : (editingOrgId ? <Pencil size={16}/> : <Plus size={16}/>)}
                            {editingOrgId ? 'Update Organization' : 'Create Organization'}
                        </button>
                    </form>
                    </div>
                </div>
            )}

            {/* --- ORG LIST (full width) --- */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {orgs.map(org => (
                        <div key={org.id} className={`bg-white p-5 rounded-2xl shadow-sm border transition-all flex flex-col gap-4 ${editingOrgId === org.id ? 'border-brand-primary ring-2 ring-brand-primary/20' : 'border-slate-200'}`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-lg font-black flex items-center gap-2">
                                        <Building2 size={20} className={org.branding?.enabled ? "text-purple-500" : "text-slate-400"}/> 
                                        {org.name}
                                    </h3>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {/* NEW: INDUSTRY BADGE */}
                                        <span className={`text-[10px] font-bold px-2 py-1 rounded border flex items-center gap-1 uppercase tracking-wider ${org.industry === 'corporate' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-orange-50 text-orange-700 border-orange-100'}`}>
                                            {org.industry === 'corporate' ? <Briefcase size={12}/> : <CheckCircle2 size={12}/>}
                                            {org.industry || 'sport'}
                                        </span>
                                        
                                        <span className="bg-blue-50 text-blue-700 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-blue-100">{org.activeAssessmentId}</span>
                                        <span className="bg-slate-100 text-slate-500 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-slate-200">Every {org.cadenceDays} Days</span>
                                        
                                        {/* SQUAD BADGE */}
                                        {org.features?.enableSquadHub && (
                                            <span className="bg-indigo-50 text-indigo-700 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-indigo-100 flex items-center gap-1">
                                                <Users size={12}/> Squads
                                            </span>
                                        )}
                                        
                                        {/* BRANDING BADGE */}
                                        {org.branding?.enabled && (
                                            <span className="bg-purple-50 text-purple-700 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border border-purple-100 flex items-center gap-1">
                                                <Palette size={12}/> Branded
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-wrap justify-end gap-2">
                                    {/* PREVIEW BUTTON */}
                                    <button 
                                        onClick={() => onPreview && onPreview(org.id)}
                                        className="px-3 py-2 bg-slate-900 text-white font-bold text-xs rounded-lg transition-colors flex items-center gap-2 hover:bg-black" 
                                        title="Impersonate Org View"
                                    >
                                        <Eye size={16} /> View
                                    </button>

                                    <button onClick={() => handleEditOrgClick(org)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors" title="Edit Details"><Pencil size={18}/></button>
                                    
                                    <button onClick={() => setAuditingOrg(org)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-lg transition-colors flex items-center gap-2" title="Audit Anonymized Data">
                                        <ClipboardList size={16} /> Audit
                                    </button>
                                    
                                    <button onClick={() => setManagingOrg(org)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-lg transition-colors flex items-center gap-2"><Users size={16} /> Teams</button>
                                    <button onClick={() => copyInviteLink(org.id)} className="p-2 text-brand-primary hover:bg-blue-50 rounded-lg transition-colors" title="Copy Invite Link"><Share2 size={18}/></button>
                                    <button onClick={() => handleDeleteOrg(org.id)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={18}/></button>
                                </div>
                            </div>
                            {org.groups && org.groups.length > 0 && (<div className="flex flex-wrap gap-2 pt-2 border-t border-slate-50 mt-2">{org.groups.map(g => (<span key={g} className="text-[10px] font-bold bg-slate-50 text-slate-500 px-2 py-1 rounded border border-slate-100">{g}</span>))}</div>)}
                        </div>
                    ))}
            </div>

            <style>{`
                /* Modified for distinct color toggles */
                .toggle-checkbox:checked { right: 0; }
                .toggle-purple:checked { border-color: #a855f7; }
                .toggle-purple:checked + .toggle-label { background-color: #a855f7; }
                .toggle-blue:checked { border-color: #3b82f6; }
                .toggle-blue:checked + .toggle-label { background-color: #3b82f6; }
                .toggle-checkbox { right: 0; z-index: 1; border-color: #cbd5e1; transition: all 0.3s; }
            `}</style>
        </div>
    );
};

export default OrganizationManager;